import { accountReturnPath } from "../zerops/navigationStorage";
/**
 * Where `app.zerops.io` sends the browser back after the user signed in there.
 *
 * Not nested under `/zerops` (hence the `zerops_` segment): this runs before
 * there is a session, so it must not mount anything that assumes one. The door
 * gives it a bare shell and never redirects it — a redirect would drop the
 * fragment, and with it the credential.
 */

import type { ZeropsHandoverOutcome } from "@t3tools/client-runtime/zerops/handover";
import { zeropsErrorMessage } from "@t3tools/client-runtime/zerops/errors";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ZeropsHandoverFailed } from "../components/zerops/landing/ZeropsHandoverFailed";
import { ZeropsFrameWait } from "../components/zerops/landing/ZeropsLandingShell";
import { completeZeropsHandover, readHandoverOnce, retryZeropsHandover } from "../zerops/handover";
import { useZeropsSession } from "../zerops/ZeropsSessionProvider";

export const Route = createFileRoute("/zerops_/authorized")({
  // Read in `beforeLoad`, not in the component: this runs before anything
  // renders, so the fragment cannot be gone by the time we look for it. It
  // runs more than once per navigation though, and the read is destructive —
  // hence `readHandoverOnce`, without which run 2 sees the scrubbed URL,
  // reports `absent`, and silently returns the user to the landing holding a
  // credential that has already been spent.
  beforeLoad: () => ({ handover: takeHandover() }),
  component: ZeropsHandoverCallback,
});

type CallbackState =
  | { readonly kind: "working" }
  | { readonly kind: "failed"; readonly message: string };

/**
 * Reads the callback, spends the nonce, and takes the fragment out of the URL
 * and browser history — in that order, so the credential is never left behind
 * for a back button or a screenshot.
 */
function takeZeropsHandoverFromLocation(): ZeropsHandoverOutcome {
  const fragment = window.location.hash;
  const outcome = completeZeropsHandover({ fragment });
  if (fragment) {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  }
  return outcome;
}

/**
 * `beforeLoad` runs more than once per navigation and the read above is
 * destructive, so it is read once and every later run gets that same answer.
 */
const takeHandover = readHandoverOnce(takeZeropsHandoverFromLocation);

/**
 * The one adoption of this load's hand-over. The callback mounts again once the account opens (the
 * account's registry replaces the hand-over's), and every mount waits on this same adoption: a
 * handed-over token is adopted once, and each mount routes on or shows its failure.
 */
let adoption: Promise<void> | null = null;

function ZeropsHandoverCallback() {
  const { handover: outcome } = Route.useRouteContext();
  const { adoptHandover } = useZeropsSession();
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>({ kind: "working" });

  useEffect(() => {
    if (outcome.kind === "absent") {
      void navigate({ to: "/zerops", replace: true });
      return;
    }
    if (outcome.kind === "mismatched") {
      setState({
        kind: "failed",
        message: "That sign-in did not come from this window. Start again from Zerops Mate.",
      });
      return;
    }
    if (outcome.kind === "declined") {
      setState({
        kind: "failed",
        message:
          outcome.code === "access_denied"
            ? "Sign-in was cancelled."
            : "Zerops could not complete that sign-in.",
      });
      return;
    }

    let current = true;
    if (adoption === null)
      adoption = adoptHandover({
        token: outcome.token,
        zcpClaimed: outcome.zcpClaimed,
      }).then(
        // The account opens in this document: the route changes, nothing reloads. The path carries
        // the base path, which the router strips, so a route named like the base keeps its own.
        () => void navigate({ href: accountReturnPath(), replace: true }),
      );
    adoption.catch((cause: unknown) => {
      if (current) setState({ kind: "failed", message: zeropsErrorMessage(cause) });
    });
    return () => {
      current = false;
    };
  }, [adoptHandover, navigate, outcome]);

  if (state.kind === "working") {
    return (
      <ZeropsFrameWait
        data-zerops-handover="working"
        label="Signing you in…"
        line="Signing you in…"
        signedIn
      />
    );
  }
  return (
    <ZeropsHandoverFailed
      message={state.message}
      onBack={() => {
        void navigate({ to: "/zerops", replace: true });
      }}
      onRetry={() => {
        window.location.href = retryZeropsHandover();
      }}
    />
  );
}
